import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #pendingMutation = false;
  #retryConfig = null;
  #disabledControls = [];

  #guardCheckout = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.cart-form')) return;
    if (event.submitter?.name === 'update') return;
    const button = event.submitter?.matches('[data-thb-cart-checkout]')
      ? event.submitter
      : this.querySelector('[data-thb-cart-checkout]');
    if (!button) return;
    const minimumBlocked = button.dataset.thbMinimumBlocked === 'true';
    const unconfirmedQuantity = [...form.querySelectorAll('input[data-cart-line]')]
      .some((input) => input.value !== input.defaultValue);
    if (!minimumBlocked && button.dataset.thbCartEmpty !== 'true' && !button.disabled &&
        !this.#pendingMutation && !document.querySelector('[data-thb-cart-pending]') && !unconfirmedQuantity) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (minimumBlocked) {
      button.closest('.cart__ctas')?.querySelector('[data-thb-cart-minimum-notice]')?.focus();
    }
  };

  retryUpdate() {
    if (this.#retryConfig) this.updateQuantity(this.#retryConfig);
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('submit', this.#guardCheckout, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('submit', this.#guardCheckout, true);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#pendingMutation || document.querySelector('[data-thb-cart-pending]')) return;
    const { quantity } = config;
    if (!Number.isInteger(Number(quantity)) || Number(quantity) < 0) return;
    const rows = [...this.querySelectorAll('[ref="cartItemRows[]"]')].filter((item) => item.closest('cart-items-component') === this);
    const row = config.key ? rows.find((item) => item.dataset.key === config.key) : rows[config.line - 1];
    if (!row) {
      this.#retryConfig = null;
      if (this.refs.cartUpdateErrorMessage) this.refs.cartUpdateErrorMessage.textContent = this.dataset.updateError;
      const retryButton = this.refs.cartUpdateError?.querySelector('button');
      if (retryButton) retryButton.hidden = true;
      this.refs.cartUpdateError?.removeAttribute('hidden');
      this.refs.cartUpdateError?.focus();
      return;
    }
    const line = rows.indexOf(row) + 1;
    this.#pendingMutation = true;
    this.dataset.thbCartPending = '';
    this.#retryConfig = { ...config, line, key: row.dataset.key };
    const retryButton = this.refs.cartUpdateError?.querySelector('button');
    if (retryButton) retryButton.hidden = false;
    this.refs.cartUpdateError?.setAttribute('hidden', '');
    this.querySelectorAll('[ref^="cartItemErrorContainer-"]').forEach((element) => element.classList.add('hidden'));
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(row.dataset.key ? { id: row.dataset.key } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    deferredUpdatePromise.promise.catch(() => {});
    const requestController = new AbortController();
    const requestTimeout = setTimeout(() => requestController.abort(), 20000);
    try {
        const response = await fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal: requestController.signal });
        const parsedResponseText = await response.json();
        if (!response.ok || parsedResponseText.errors || parsedResponseText.status >= 400) {
          const detail = parsedResponseText.errors || parsedResponseText.description;
          const error = new Error(typeof detail === 'string' ? detail : this.dataset.updateError);
          error.cartValidation = response.status === 422 || Boolean(parsedResponseText.errors);
          throw error;
        }
        const sectionHTML = parsedResponseText.sections?.[this.sectionId];
        if (!Array.isArray(parsedResponseText.items) || !Number.isInteger(parsedResponseText.item_count) || parsedResponseText.item_count < 0 || typeof sectionHTML !== 'string') {
          throw new Error('Incomplete cart response');
        }
        if (parsedResponseText.items.some((item) => !item.variant_id || !item.key || !Number.isInteger(item.quantity) || item.quantity < 1)) throw new Error('Invalid cart items');
        const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
        if (!newSectionHTML.getElementById(`shopify-section-${this.sectionId}`)?.querySelector('cart-items-component')) throw new Error('Invalid cart section');

        const newCartItemCount = parsedResponseText.item_count;

        await morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        this.#retryConfig = null;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        this.#updateCartQuantitySelectorButtonStates();
      } catch (error) {
        deferredUpdatePromise.reject(error);
        this.#handleCartError(line, {
          errors: error.cartValidation ? error.message : this.dataset.updateError,
        });
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      } finally {
        clearTimeout(requestTimeout);
        resetShimmer(this);
        this.#pendingMutation = false;
        delete this.dataset.thbCartPending;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      }
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    if (this.refs.cartUpdateErrorMessage) this.refs.cartUpdateErrorMessage.textContent = parsedResponseText.errors || this.dataset.updateError;
    this.refs.cartUpdateError?.removeAttribute('hidden');
    this.refs.cartUpdateError?.focus();
    this.#updateCartQuantitySelectorButtonStates();
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
    this.#disabledControls = [...document.querySelectorAll('cart-items-component button, cart-items-component input, cart-items-component select, cart-items-component textarea')]
      .map((element) => ({ element, disabled: element.disabled, minimumBlocked: element.dataset.thbMinimumBlocked, cartEmpty: element.dataset.thbCartEmpty }));
    for (const { element } of this.#disabledControls) element.disabled = true;
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
    for (const { element, disabled, minimumBlocked, cartEmpty } of this.#disabledControls) {
      if (!element.isConnected) continue;
      if (element.hasAttribute('data-thb-cart-checkout')) {
        const ruleChanged = minimumBlocked !== element.dataset.thbMinimumBlocked || cartEmpty !== element.dataset.thbCartEmpty;
        element.disabled = element.dataset.thbMinimumBlocked === 'true' || element.dataset.thbCartEmpty === 'true' || (!ruleChanged && disabled);
      } else {
        element.disabled = disabled;
      }
    }
    this.#disabledControls = [];
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const variantQuantities = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      variantQuantities.set(variantId, (variantQuantities.get(variantId) || 0) + item.quantity);
    }

    for (const selector of document.querySelectorAll('quantity-selector-component[data-variant-id]')) {
      const input = selector.querySelector('input[data-cart-quantity]');
      if (!input) continue;

      const quantity = variantQuantities.get(selector.getAttribute('data-variant-id')) || 0;
      input.setAttribute('data-cart-quantity', quantity.toString());

      if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
        selector.updateCartQuantity();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
